import React from 'react';
import renderLineItem from 'components/labelLineItem';
import { useLocale, WithLocaleProps } from 'locale';
import {
  getBuildingClassKey,
  BuildingClass,
} from 'utils/entities';
import {
  PropertyDetailsText,
  NewPropertyDetailsSeparator,
  PropertyDetailsSeparator,
} from 'ds/components/ListingCard';
import { BreakpointValue, useScreenBreakpoint } from 'consts/breakpoints';
import { CommercialBulletinUniversalCardProps, ExtraProps } from 'components/cards/types';
import { UniversalCardLineLabels } from 'components/cards/listing/UniversalCard/utils';


export interface UniversalCommercialBulletinCardProps extends CommercialBulletinUniversalCardProps, ExtraProps {
  breakpoint: BreakpointValue;
  carousel: JSX.Element;
  handleOnMouseEnter: () => void;
  handleOnMouseLeave: () => void;
}

const getInfoLineConfig = (area: number, price: number, size: number, floor: number, buildingClass: BuildingClass, { t, formatArea, formatMoney }: WithLocaleProps, breakpoint: BreakpointValue) => {
  const isMobile = breakpoint <= 2;
  const separator = isMobile ? <NewPropertyDetailsSeparator /> : <PropertyDetailsSeparator isPromoted={undefined} />;
  return [
    {
      label: UniversalCardLineLabels.BuildingClass,
      separator,
      showCondition: Boolean(buildingClass),
      render: () => (
        <PropertyDetailsText data-auto="property-class">
          {t('unitPage.buildingClass', { buildingClass: getBuildingClassKey(buildingClass), isCommercial: true })}
        </PropertyDetailsText>
      ),
    },
    {
      label: UniversalCardLineLabels.Size,
      separator,
      showCondition: Boolean(size),
      render: () => (
        <PropertyDetailsText data-auto="property-size">
          {formatArea(size)}
        </PropertyDetailsText>
      ),
    },
    {
      label: UniversalCardLineLabels.PPM,
      separator,
      showCondition: Boolean(area && price),
      render: () => (
        <PropertyDetailsText data-auto="property-ppm">
          {t('widget.prices.pricePerSqMeter', { price: formatMoney(price / area) })}
        </PropertyDetailsText>
      ),
    },
    {
      label: UniversalCardLineLabels.Floor,
      separator,
      showCondition: Boolean(floor),
      render: () => (
        <PropertyDetailsText data-auto="property-floor">
          {t('listingCard.floorLabel', { floor })}
        </PropertyDetailsText>
      ),
    },
  ];
};

interface CommercialBulletinLineProps {
  area: number;
  price: number;
  size?: number;
  floor: number;
  buildingClass: BuildingClass;
}

export const CommercialBulletinLine: React.FC<CommercialBulletinLineProps> = ({
  area,
  price,
  size,
  floor,
  buildingClass,
}) => {
  const localeProps = useLocale();
  const breakpoint = useScreenBreakpoint();
  const infoLineConfig = React.useMemo(() => {
    return getInfoLineConfig(area, price, size, floor, buildingClass, localeProps, breakpoint).filter(({ showCondition }) => showCondition);
  }, [ localeProps, breakpoint, getInfoLineConfig ]);

  return (
    <>
      {infoLineConfig.map(renderLineItem)}
    </>
  );
};
